import React from "react";
import { Truck, PackageCheck, Undo2 } from "lucide-react";
import { catColor } from "@/lib/constants";
import { DAYS, ymd, STATUS_STYLE, eventTime } from "@/components/calendar/calendarUtils";

function EventCard({ e, onClick, tall, draggable }) {
  const st = STATUS_STYLE[e.status] || STATUS_STYLE.prospect;
  const time = eventTime(e);
  return (
    <button data-testid={`cal-event-${e.id}`} onClick={() => onClick(e.id)} title={e.title}
      draggable={draggable} onDragStart={(ev) => { ev.dataTransfer.setData("text/event-id", e.id); ev.dataTransfer.effectAllowed = "move"; }}
      className={`group block w-full rounded-lg border px-2 py-1.5 text-left shadow-sm transition-[border-color,transform] hover:-translate-y-px hover:border-[#FF6A00]/60 ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${st.card || "border-zinc-800 bg-zinc-950/60"}`}>
      <span className="flex items-center gap-1.5 text-xs font-semibold">
        <st.icon className={`h-3.5 w-3.5 shrink-0 ${st.color}`} strokeWidth={2.2} />
        <span className={st.card ? st.color : "text-white"}>{time || "Journée"}</span>
        <span className={`ml-auto h-2 w-2 shrink-0 rounded-full ${catColor(e.category).dot}`} title={catColor(e.category).label} />
      </span>
      <span className={`mt-0.5 block text-xs text-zinc-400 ${tall ? "line-clamp-2" : "truncate"}`}>{e.title}</span>
    </button>
  );
}

const LOGI = [
  ["load_at", "bg-[#FF6A00]", Truck, "Chargement"],
  ["delivery_at", "bg-blue-500", PackageCheck, "Livraison"],
  ["pickup_at", "bg-emerald-500", Undo2, "Récupération"],
];

function LogiPills({ events, ds }) {
  const pills = LOGI.map(([f, c, I, l]) => ({ c, I, list: events.filter((e) => (e[f] || "").slice(0, 10) === ds), l })).filter((p) => p.list.length);
  if (!pills.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid={`cal-logi-${ds}`}>
      {pills.map((p) => (
        <span key={p.l} title={`${p.l} : ${p.list.map((e) => e.title).join(", ")}`} className={`inline-flex items-center gap-0.5 rounded-full ${p.c} px-1 py-0.5 text-[9px] font-bold text-white`}>
          <p.I className="h-2.5 w-2.5" />{p.list.length > 1 ? p.list.length : ""}
        </span>
      ))}
    </div>
  );
}

export default function CalendarGrid({ mode, days, month, events, logiEvents, onEventClick, onMore, onMove }) {
  const [over, setOver] = React.useState("");
  const week = mode === "week";
  const today = ymd(new Date());
  const max = week ? Infinity : 2;
  return (
    <div className="overflow-x-auto" data-testid="calendar-grid">
      <div className="min-w-[700px]">
        <div className="grid grid-cols-7 border-b border-zinc-800">
          {DAYS.map((d, i) => (
            <div key={d} className={`py-2.5 text-center text-xs font-medium text-zinc-400 ${i ? "border-l border-zinc-800" : ""}`}>
              {d}{week && <span className="ml-1 text-zinc-600">{days[i].getDate()}</span>}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((dt, i) => {
            const ds = ymd(dt);
            const out = !week && dt.getMonth() !== month;
            const list = events.filter((e) => (e.date || "").slice(0, 10) === ds).sort((a, b) => (eventTime(a) || "99").localeCompare(eventTime(b) || "99"));
            return (
              <div key={ds} data-testid={`cal-day-${ds}`}
                onDragOver={onMove ? (ev) => { ev.preventDefault(); if (over !== ds) setOver(ds); } : undefined}
                onDragLeave={onMove ? () => setOver((o) => (o === ds ? "" : o)) : undefined}
                onDrop={onMove ? (ev) => { ev.preventDefault(); setOver(""); const id = ev.dataTransfer.getData("text/event-id"); if (id) onMove(id, ds); } : undefined}
                className={`p-2 transition-colors hover:bg-white/[0.02] ${over === ds ? "bg-[#FF6A00]/10 ring-2 ring-inset ring-[#FF6A00]/60" : ""} ${week ? "min-h-[420px]" : "min-h-[118px]"} ${i % 7 ? "border-l border-zinc-800" : ""} ${i >= 7 ? "border-t border-zinc-800" : ""} ${out ? "bg-zinc-950/50" : ""}`}>
                <span className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs font-medium ${ds === today ? "bg-[#FF6A00] text-white" : out ? "text-zinc-700" : "text-zinc-500"}`}>{dt.getDate()}</span>
                <div className="mt-1.5 space-y-1.5">
                  {list.slice(0, max).map((e) => <EventCard key={e.id} e={e} onClick={onEventClick} tall={week} draggable={!!onMove} />)}
                  {list.length > max && (
                    <button data-testid={`cal-more-${ds}`} onClick={() => onMore(dt)} className="px-1 text-xs font-medium text-zinc-500 transition-colors hover:text-[#FF6A00]">+{list.length - max} autre{list.length - max > 1 ? "s" : ""}</button>
                  )}
                </div>
                <LogiPills events={logiEvents} ds={ds} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
